import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useGetRequestLinkQuery } from "../../../slices/ClientRequestsSlice";
import { usePortalSendCodeMutation } from "../../../slices/PortalSlice";
import {
  PortalAlert,
  PortalAuthFrame,
  portalLink,
  portalPrimary,
} from "../components/PortalUi";
import { errorMessage } from "../portal.utils";
import {
  getPortalSession,
  rememberEmail,
  rememberFirm,
} from "../portalSession";

/** From a request list email: sign in (email code, or password) and land on the list. */
export const PortalRequestLink = () => {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const link = useGetRequestLinkQuery(token, { skip: !token });
  const [sendCode, sendState] = usePortalSendCodeMutation();
  const [error, setError] = useState<string | null>(null);
  const info = link.data;
  const next = info ? `/portal/requests/${info.requestId}` : "";

  const emailCode = async () => {
    if (!info) return;
    setError(null);
    rememberFirm(info.firmName);
    rememberEmail(info.email);
    const result = await sendCode({ email: info.email });
    if ("error" in result) return setError(errorMessage(result.error));
    navigate(
      `/portal/code?email=${encodeURIComponent(
        info.email,
      )}&next=${encodeURIComponent(next)}`,
    );
  };

  if (link.isLoading)
    return (
      <PortalAuthFrame>
        <span className={"text-sm font-semibold text-[#6B7185]"}>Opening…</span>
      </PortalAuthFrame>
    );

  if (!info)
    return (
      <PortalAuthFrame>
        <h1 className={"m-0 text-2xl font-extrabold"}>
          This link doesn’t work
        </h1>
        <PortalAlert>
          {errorMessage(link.error, "This link isn’t valid anymore.")}
        </PortalAlert>
        <Link to={"/portal"} className={portalLink}>
          Go to the client portal
        </Link>
      </PortalAuthFrame>
    );

  return (
    <PortalAuthFrame firmName={info.firmName}>
      <div className={"flex flex-col gap-1.5"}>
        <h1 className={"m-0 text-2xl font-extrabold tracking-[-0.4px]"}>
          {info.firmName} needs a few things from you
        </h1>
        <span className={"text-sm font-medium leading-relaxed text-[#6B7185]"}>
          <strong className={"text-[#1F2433]"}>{info.title}</strong>
        </span>
      </div>
      {getPortalSession() ? (
        <button
          type={"button"}
          className={portalPrimary}
          onClick={() => navigate(next)}>
          Open the list
        </button>
      ) : (
        <>
          <span
            className={"text-sm font-medium leading-relaxed text-[#3A4256]"}>
            For your security, we’ll email a 6-digit code to{" "}
            <strong>{info.email}</strong>.
          </span>
          {error && <PortalAlert>{error}</PortalAlert>}
          <button
            type={"button"}
            className={portalPrimary}
            onClick={emailCode}
            disabled={sendState.isLoading}>
            {sendState.isLoading ? "Sending…" : "Email me a code"}
          </button>
          {/* Password sign-in lands on portal home, whose "Requests" card opens the list. */}
          <Link to={"/portal"} className={`${portalLink} text-center text-sm`}>
            Sign in with my password instead
          </Link>
        </>
      )}
    </PortalAuthFrame>
  );
};
